import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
    Slide
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { SaveButton, CancelButton, ConfirmButton, CloseButton, DeleteButton, AddButton } from './StandardActionButtons';
import { useThemeColors } from '../hooks/useThemeColors';
import { useTranslation } from 'react-i18next';

// Transizione animata per i dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Hook per i colori dei dialog standardizzati (manteniamo per backward compatibility)
export const useDialogColors = () => {
    const { dialog } = useThemeColors();
    return dialog;
};

// Componente Dialog standardizzato base
export const StandardDialog = ({
    open = false,
    onClose,
    title,
    subtitle,
    children,
    maxWidth = 'sm',
    fullWidth = true,
    fullScreen = false,
    showCloseButton = true,
    disableBackdropClick = false,
    TransitionComponent = Transition,
    sx = {},
    titleSx = {},
    contentSx = {},
    ...props
}) => {
    const theme = useTheme();
    const dialogColors = useDialogColors();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClose = (event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return;
        }
        onClose?.(event, reason);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={fullScreen || isMobile}
            TransitionComponent={TransitionComponent}
            PaperProps={{
                sx: {
                    backgroundColor: dialogColors.background,
                    borderRadius: { xs: 0, sm: 2 },
                    maxHeight: '90vh',
                    ...sx
                }
            }}
            {...props}
        >
            {title && (
                <DialogTitle
                    sx={{
                        backgroundColor: dialogColors.headerBackground,
                        color: dialogColors.headerText,
                        borderBottom: `1px solid ${dialogColors.border}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        ...titleSx
                    }}
                >
                    <Box>
                        <Typography variant="h6" component="div">
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="subtitle2" sx={{ opacity: 0.8, mt: 0.5 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {showCloseButton && (
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: dialogColors.titleText,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <Close />
                        </IconButton>
                    )}
                </DialogTitle>
            )}
            
            <DialogContent
                sx={{
                    backgroundColor: dialogColors.background,
                    color: theme.palette.text.primary,
                    padding: { xs: 2, sm: 3 },
                    ...contentSx
                }}
            >
                {children}
            </DialogContent>
        </Dialog>
    );
};

// Dialog per form con azioni standardizzate
export const FormDialog = ({
    open = false,
    onClose,
    onSave,
    title,
    subtitle,
    children,
    saveText,
    cancelText,
    saveDisabled = false,
    isEditing = false,
    isLoading = false,
    showCancelButton = true,
    additionalActions,
    ...props
}) => {
    const { t } = useTranslation('global');
    const actualSaveText = saveText || t('common.save', 'Salva');
    const actualCancelText = cancelText || t('common.cancel', 'Annulla');
    
    const handleSave = (event) => {
        if (onSave) {
            onSave(event);
        }
    };

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            {...props}
        >
            {children}
            
            <DialogActions
                sx={{
                    padding: 2,
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                    gap: 1
                }}
            >
                {additionalActions}
                {showCancelButton && (
                    <CancelButton onClick={onClose}>
                        {actualCancelText}
                    </CancelButton>
                )}
                <SaveButton
                    onClick={handleSave}
                    disabled={saveDisabled || isLoading}
                    loading={isLoading}
                >
                    {isEditing ? t('common.update', 'Aggiorna') : actualSaveText}
                </SaveButton>
            </DialogActions>
        </StandardDialog>
    );
};

// Dialog di conferma per operazioni pericolose
export const ConfirmationDialog = ({
    open = false,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    severity = "warning", // "warning", "error", "info"
    confirmButtonColor = "primary",
    children,
    ...props
}) => {
    const { t } = useTranslation('global');
    const actualTitle = title || t('dialogs.confirmOperation', 'Conferma operazione');
    const actualConfirmText = confirmText || t('common.confirm', 'Conferma');
    const actualCancelText = cancelText || t('common.cancel', 'Annulla');
    
    const handleConfirm = () => {
        onConfirm?.();
        onClose?.();
    };

    // Determina il colore del bottone in base alla severity
    const getConfirmButtonProps = () => {
        switch (severity) {
            case "error":
                return { color: "error", variant: "contained" };
            case "warning":
                return { color: "warning", variant: "contained" };
            default:
                return { color: confirmButtonColor, variant: "contained" };
        }
    };

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            title={actualTitle}
            maxWidth="xs"
            showCloseButton={false}
            {...props}
        >
            {message && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {message}
                </Typography>
            )}
            {children}
            
            <DialogActions sx={{ padding: 2, gap: 1 }}>
                <CancelButton onClick={onClose}>
                    {actualCancelText}
                </CancelButton>
                <ConfirmButton 
                    onClick={handleConfirm}
                    {...getConfirmButtonProps()}
                >
                    {actualConfirmText}
                </ConfirmButton>
            </DialogActions>
        </StandardDialog>
    );
};

// Dialog di conferma per eliminazione
export const DeleteConfirmationDialog = ({
    open = false,
    onClose,
    onConfirm,
    title,
    itemName,
    message,
    ...props
}) => {
    const { t } = useTranslation('global');
    const actualTitle = title || t('dialogs.confirmDelete', 'Conferma eliminazione');
    const defaultMessage = itemName 
        ? t('dialogs.deleteItemConfirm', `Sei sicuro di voler eliminare "${itemName}"? Questa operazione non può essere annullata.`, { itemName })
        : t('dialogs.deleteConfirm', 'Sei sicuro di voler eliminare questo elemento? Questa operazione non può essere annullata.');

    return (
        <ConfirmationDialog
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            title={actualTitle}
            message={message || defaultMessage}
            confirmText={t('common.delete', 'Elimina')}
            cancelText={t('common.cancel', 'Annulla')}
            severity="error"
            {...props}
        />
    );
};

// Dialog per visualizzazione di dettagli (sola lettura)
export const DetailDialog = ({
    open = false,
    onClose,
    title,
    subtitle,
    children,
    closeText = "Chiudi",
    ...props
}) => {
    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            {...props}
        >
            {children}
            
            <DialogActions sx={{ padding: 2 }}>
                <CloseButton onClick={onClose}>
                    {closeText}
                </CloseButton>
            </DialogActions>
        </StandardDialog>
    );
};

// Dialog per selezione multipla
export const SelectionDialog = ({
    open = false,
    onClose,
    onConfirm,
    title,
    children,
    confirmText = "Seleziona",
    cancelText = "Annulla",
    confirmDisabled = false,
    selectedCount = 0,
    ...props
}) => {
    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            title={title}
            subtitle={selectedCount > 0 ? `${selectedCount} elementi selezionati` : undefined}
            {...props}
        >
            {children}
            
            <DialogActions sx={{ padding: 2, gap: 1 }}>
                <CancelButton onClick={onClose}>
                    {cancelText}
                </CancelButton>
                <ConfirmButton
                    onClick={onConfirm}
                    disabled={confirmDisabled}
                >
                    {confirmText}
                </ConfirmButton>
            </DialogActions>
        </StandardDialog>
    );
};

// Dialog per upload file
export const FileUploadDialog = ({
    open = false,
    onClose,
    onUpload,
    title = "Carica file",
    acceptedTypes = "image/*",
    multiple = false,
    children,
    ...props
}) => {
    const [selectedFiles, setSelectedFiles] = React.useState([]);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    const handleUpload = () => {
        if (selectedFiles.length > 0) {
            onUpload?.(selectedFiles);
            setSelectedFiles([]);
            onClose?.();
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        onClose?.();
    };

    return (
        <StandardDialog
            open={open}
            onClose={handleClose}
            title={title}
            {...props}
        >
            <Box sx={{ py: 2 }}>
                <input
                    accept={acceptedTypes}
                    style={{ display: 'none' }}
                    id="file-upload-input"
                    type="file"
                    multiple={multiple}
                    onChange={handleFileSelect}
                />
                <label htmlFor="file-upload-input">
                    <AddButton component="span" fullWidth>
                        Seleziona {multiple ? "file" : "file"}
                    </AddButton>
                </label>
                
                {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            File selezionati:
                        </Typography>
                        {selectedFiles.map((file, index) => (
                            <Typography key={index} variant="body2" color="textSecondary">
                                • {file.name}
                            </Typography>
                        ))}
                    </Box>
                )}
                
                {children}
            </Box>
            
            <DialogActions sx={{ padding: 2, gap: 1 }}>
                <CancelButton onClick={handleClose}>
                    Annulla
                </CancelButton>
                <SaveButton
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0}
                >
                    Carica
                </SaveButton>
            </DialogActions>
        </StandardDialog>
    );
};

// Dialog responsivo che si adatta a mobile
export const ResponsiveDialog = ({
    open = false,
    breakpoint = 'sm',
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));

    return (
        <StandardDialog
            open={open}
            fullScreen={isMobile}
            {...props}
        />
    );
};

// Export di tutti i componenti
export default StandardDialog;